import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Volume2, Languages, Trash2, ArrowLeft, ImagePlus, X } from "lucide-react";

export default function AdminQuestionEdit() {
  const { qid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regenLang, setRegenLang] = useState(null);
  const [regenAudio, setRegenAudio] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgVer, setImgVer] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => { api.get(`/questions/${qid}?lang=pt`).then((r) => setQ(r.data)); }, [qid]);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG or WebP images are allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/questions/${qid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQ((p) => ({ ...p, image_file: r.data.image_file }));
      setImgVer((v) => v + 1);
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async () => {
    if (!window.confirm("Remove this question's image?")) return;
    try {
      await api.delete(`/admin/questions/${qid}/image`);
      setQ((p) => ({ ...p, image_file: null }));
      toast.success("Image removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const setField = (k, v) => setQ({ ...q, [k]: v });
  const setOption = (i, text) => setQ({ ...q, options_pt: q.options_pt.map((o, ix) => ix === i ? { ...o, text } : o) });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/questions/${qid}`, {
        text_pt: q.text_pt,
        options_pt: q.options_pt,
        correct_letter: q.correct_letter,
        explanation_pt: q.explanation_pt,
        category_code: q.category_code,
      });
      toast.success("Saved · translations invalidated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this question forever?")) return;
    await api.delete(`/admin/questions/${qid}`);
    toast.success("Deleted");
    nav("/admin/questions");
  };

  const regenTranslation = async (lang) => {
    setRegenLang(lang);
    try {
      await api.post(`/admin/questions/${qid}/regenerate-translation`, { lang });
      toast.success(`${lang.toUpperCase()} translation regenerated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRegenLang(null); }
  };

  const regenAudioNow = async () => {
    setRegenAudio(true);
    try {
      await api.post(`/admin/questions/${qid}/regenerate-audio`);
      toast.success("Audio regenerated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRegenAudio(false); }
  };

  if (!q) return <AdminLayout title="Question"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></AdminLayout>;

  return (
    <AdminLayout title={`Edit ${q.id}`} actions={
      <>
        <Button variant="ghost" onClick={() => nav("/admin/questions")} className="text-white/70" data-testid="back-questions"><ArrowLeft className="w-4 h-4 mr-1" /> All questions</Button>
        <Button onClick={save} disabled={saving} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="save-question">
          {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
        </Button>
      </>
    }>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white/4 border border-white/10 rounded-lg p-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Category code</Label>
                <Input value={q.category_code} onChange={(e) => setField("category_code", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1.5" data-testid="cat-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Correct letter</Label>
                <div className="flex gap-1.5 mt-1.5">
                  {["A", "B", "C", "D"].map((l) => (
                    <button key={l} onClick={() => setField("correct_letter", l)} data-testid={`correct-${l}`}
                      className={`flex-1 h-10 rounded-md font-mono font-semibold border transition-colors ${
                        q.correct_letter === l ? "bg-[hsl(var(--accent))] text-black border-transparent" : "bg-white/5 border-white/15 text-white/70 hover:border-white/40"
                      }`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Question text (PT)</Label>
              <Textarea value={q.text_pt} onChange={(e) => setField("text_pt", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1.5 min-h-24" data-testid="text-pt-input" />
            </div>
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Options (PT)</Label>
              {q.options_pt.map((o, i) => (
                <div key={o.letter} className="flex gap-3 items-start">
                  <div className={`font-mono font-semibold w-8 h-9 flex items-center justify-center rounded-md ${q.correct_letter === o.letter ? "bg-emerald-500/20 text-emerald-400" : "bg-white/5 text-white/60"}`}>{o.letter}</div>
                  <Textarea value={o.text} onChange={(e) => setOption(i, e.target.value)} className="bg-white/5 border-white/15 text-white flex-1 min-h-16" data-testid={`opt-input-${o.letter}`} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Explanation (PT, optional)</Label>
              <Textarea value={q.explanation_pt || ""} onChange={(e) => setField("explanation_pt", e.target.value)} className="bg-white/5 border-white/15 text-white mt-1.5" data-testid="explanation-input" />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white/4 border border-white/10 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">// media</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} data-testid="edit-image-input" />
            {q.image_file && (
              <div className="relative mb-3">
                <img src={`${api.defaults.baseURL}/questions/${q.id}/image?v=${imgVer}`} alt="" className="rounded border border-white/10 max-h-48" />
                <button onClick={removeImage} data-testid="remove-image"
                  className="absolute top-2 right-2 bg-black/70 rounded-full p-1 text-white/80 hover:text-white" title="Remove image">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full mb-2 text-white bg-white/5 border-white/15" data-testid="upload-image">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
              {q.image_file ? "Replace image" : "Upload image"}
            </Button>
            <Button variant="outline" onClick={regenAudioNow} disabled={regenAudio} className="w-full text-white bg-white/5 border-white/15" data-testid="regen-audio">
              {regenAudio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
              Regenerate audio (PT)
            </Button>
          </div>

          <div className="bg-white/4 border border-white/10 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">// translations</div>
            {["en", "ur"].map((lg) => (
              <Button key={lg} variant="outline" onClick={() => regenTranslation(lg)} disabled={regenLang === lg} className="w-full mb-2 text-white bg-white/5 border-white/15" data-testid={`regen-tr-${lg}`}>
                {regenLang === lg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
                Regenerate {lg.toUpperCase()}
              </Button>
            ))}
          </div>

          <div className="bg-red-500/8 border border-red-500/20 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-red-400/70 mb-3">// danger zone</div>
            <Button variant="outline" onClick={del} className="w-full text-red-400 bg-red-500/5 border-red-500/30 hover:bg-red-500/10" data-testid="delete-question">
              <Trash2 className="w-4 h-4 mr-2" /> Delete question
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
